import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getAccountConfig, updateAccountConfig, removeSpotifyAccount, getSpotifyPlaylists, setSpotifyPlaylist } from '../../api/auth.api';
import { ROUTES } from '../../config/routes';
import { useAutosave } from '../../shared/hooks/useAutosave';
import { useAuth } from '../auth/AuthContext';
import { useSubscription } from './useSubscription';
import { AlbumArt } from '../../shared/components/AlbumArt';
import { SpotifyLoginButton } from '../auth/providers/SpotifyLoginButton';
import { ArrowLeftIcon, CheckIcon, CloseIcon, TrashIcon } from '../../shared/components/icons';

// Grano de película sutil sobre el fondo (mismo que RoomView).
const GRAIN = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

const CARD = 'rounded-lg border border-lounge-line bg-lounge-card p-5 shadow-sleeve sm:p-6';

function Wordmark({ className = '' }) {
  return (
    <span className={`font-inter font-semibold uppercase tracking-[0.32em] text-lounge-text ${className}`}>
      Rockcola
    </span>
  );
}

const GENRE_GROUPS = [
  {
    label: 'General',
    genres: [
      { value: 'pop',        label: 'Pop' },
      { value: 'rock',       label: 'Rock' },
      { value: 'electronic', label: 'Electronic' },
      { value: 'hip-hop',    label: 'Hip-Hop' },
      { value: 'reggaeton',  label: 'Reggaetón' },
      { value: 'latin',      label: 'Latin' },
      { value: 'jazz',       label: 'Jazz' },
      { value: 'r&b',        label: 'R&B' },
      { value: 'metal',      label: 'Metal' },
      { value: 'funk',       label: 'Funk' },
      { value: 'soul',       label: 'Soul' },
      { value: 'blues',      label: 'Blues' },
      { value: 'country',    label: 'Country' },
      { value: 'folk',       label: 'Folk' },
      { value: 'indie',      label: 'Indie' },
      { value: 'punk',       label: 'Punk' },
      { value: 'classical',  label: 'Clásica' },
      { value: 'reggae',     label: 'Reggae' },
      { value: 'dance',      label: 'Dance' },
      { value: 'tropical',   label: 'Tropical' },
    ],
  },
  {
    label: 'Peruanos 🇵🇪',
    genres: [
      { value: 'chicha',               label: 'Chicha' },
      { value: 'cumbia peruana',        label: 'Cumbia Peruana' },
      { value: 'cumbia amazonica',      label: 'Cumbia Amazónica' },
      { value: 'huayno',               label: 'Huayno' },
      { value: 'musica criolla',        label: 'Música Criolla' },
      { value: 'musica afroperuana',    label: 'Afroperuana' },
      { value: 'folklore peruano',      label: 'Folklore Peruano' },
      { value: 'classic peruvian pop',  label: 'Pop Peruano' },
      { value: 'nueva ola peruana',     label: 'Nueva Ola Peruana' },
      { value: 'peruvian hip hop',      label: 'Hip Hop Peruano' },
      { value: 'trap peruano',          label: 'Trap Peruano' },
      { value: 'peruvian rock',         label: 'Rock Peruano' },
    ],
  },
];

const GENRE_MODES = [
  { value: 'none',    label: 'Sin filtro',           desc: 'Aparecen canciones de cualquier género.' },
  { value: 'allowed', label: 'Solo permitidos',        desc: 'Solo aparecen canciones de los géneros seleccionados.' },
  { value: 'banned',  label: 'Bloquear',             desc: 'Se ocultan las canciones de los géneros seleccionados.' },
];

function GenreGrid({ selected, onChange, mode }) {
  const toggle = (value) =>
    onChange((prev) =>
      prev.includes(value) ? prev.filter((g) => g !== value) : [...prev, value]
    );
  const activeClass = mode === 'banned'
    ? 'border-stop/50 bg-stop/10 text-stop-soft'
    : 'border-lounge-brass/50 bg-lounge-brass/15 text-lounge-brass';
  const ActiveIcon = mode === 'banned' ? CloseIcon : CheckIcon;

  return (
    <div className="flex flex-col gap-5">
      {GENRE_GROUPS.map(({ label: groupLabel, genres }) => (
        <div key={groupLabel}>
          <p className="mb-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-lounge-stone">{groupLabel}</p>
          <div className="flex flex-wrap gap-2">
            {genres.map(({ value, label }) => {
              const active = selected.includes(value);
              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={active}
                  onClick={() => toggle(value)}
                  className={
                    'inline-flex min-h-[36px] items-center gap-1.5 rounded-full border px-3.5 text-sm transition-colors ' +
                    (active
                      ? activeClass
                      : 'border-lounge-line bg-lounge-raised text-lounge-stone hover:text-lounge-text')
                  }
                >
                  {active && <ActiveIcon className="h-3.5 w-3.5" />}
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {selected.length > 0 && (
        <button
          type="button"
          onClick={() => onChange([])}
          className="self-start font-mono text-[11px] uppercase tracking-[0.15em] text-lounge-stone underline
                     underline-offset-4 transition-colors hover:text-lounge-text"
        >
          Limpiar selección
        </button>
      )}
    </div>
  );
}

const PLAYLIST_ERRORS = {
  reconnect_required: 'Esta cuenta se conectó antes de que pidiéramos permiso para leer playlists. Reconéctala con la misma cuenta de Spotify; tu QR principal y tu configuración no cambian.',
  vip_required: 'La playlist es una función del plan VIP.',
};

// Playlist VIP: con una playlist elegida, las salas de esta cuenta solo ofrecen sus canciones.
function PlaylistSection({ spotifyUserId, playlist, onChange }) {
  const [options, setOptions] = useState(null); // null = selector cerrado
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState('');
  const [reconnect, setReconnect] = useState(false); // reconnect_required: botón para renovar permisos

  const run = async (fn) => {
    setBusy(true);
    setError('');
    setReconnect(false);
    try {
      await fn();
    } catch (err) {
      const code = err.response?.data?.message;
      setError(PLAYLIST_ERRORS[code] ?? 'No se pudo completar. Intenta de nuevo.');
      setReconnect(code === 'reconnect_required');
    } finally {
      setBusy(false);
    }
  };

  const open   = () => run(async () => setOptions(await getSpotifyPlaylists(spotifyUserId)));
  const choose = (id) => run(async () => { onChange(await setSpotifyPlaylist(spotifyUserId, id)); setOptions(null); });

  const linkBtn = 'font-mono text-[11px] uppercase tracking-[0.15em] text-lounge-stone underline underline-offset-4 ' +
    'transition-colors hover:text-lounge-text disabled:opacity-50';

  return (
    <section aria-labelledby="playlist-title" className={CARD}>
      <div className="flex items-center gap-3">
        <h2 id="playlist-title" className="font-serif text-[22px] font-medium">Playlist</h2>
        <span className="rounded-full border border-lounge-brass/50 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-lounge-brass">VIP</span>
      </div>
      <p className="mt-2 text-sm leading-relaxed text-lounge-stone">
        Tus clientes solo podrán elegir canciones de esta playlist (hasta 1000 canciones).
      </p>

      {playlist ? (
        <div className="mt-5 flex items-center justify-between gap-3 rounded-md border border-lounge-brass/40 bg-lounge-base px-4 py-3">
          <div className="min-w-0">
            <p className="truncate font-medium text-lounge-text">{playlist.name}</p>
            <p className="font-mono text-[11px] tracking-wider text-lounge-stone">{playlist.total} canciones</p>
          </div>
          <div className="flex shrink-0 gap-4">
            {!options && <button type="button" onClick={open} disabled={busy} className={linkBtn}>Cambiar</button>}
            <button type="button" onClick={() => choose(null)} disabled={busy} className={linkBtn}>Quitar</button>
          </div>
        </div>
      ) : (
        !options && (
          <button type="button" onClick={open} disabled={busy}
                  className="mt-5 rounded bg-lounge-brass px-4 py-2.5 text-sm font-semibold text-lounge-base
                             transition-colors hover:brightness-110 disabled:opacity-50">
            {busy ? 'Cargando…' : 'Elegir playlist'}
          </button>
        )
      )}

      {options && (
        <div className="mt-5">
          {options.length === 0 ? (
            <p className="text-sm text-lounge-stone">No encontramos playlists creadas por esta cuenta.</p>
          ) : (
            <ul className="flex max-h-80 flex-col gap-1 overflow-y-auto" aria-busy={busy}>
              {options.map((p) => (
                <li key={p.id}>
                  <button type="button" onClick={() => choose(p.id)} disabled={busy}
                          className="flex w-full items-center gap-3 rounded p-2 text-left transition-colors
                                     hover:bg-lounge-raised disabled:opacity-50">
                    <AlbumArt image={p.image} className="w-12" />
                    <div className="min-w-0">
                      <p className="truncate text-sm text-lounge-text">{p.name}</p>
                      <p className="font-mono text-[11px] tracking-wider text-lounge-stone">{p.total} canciones</p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <button type="button" onClick={() => setOptions(null)} disabled={busy} className={`mt-3 ${linkBtn}`}>
            Cancelar
          </button>
        </div>
      )}

      {busy && options && <p role="status" className="mt-3 font-mono text-[11px] tracking-wider text-lounge-stone">Guardando playlist…</p>}
      {error && (
        <p role="alert" className="mt-4 rounded border border-stop/30 bg-stop/10 px-4 py-2.5 text-sm text-stop-soft">
          {error}
        </p>
      )}
      {/* Al cerrar el popup reintenta abrir la lista: si ya tiene los permisos, aparecen las playlists */}
      {reconnect && (
        <div className="mt-3">
          <SpotifyLoginButton spotifyUserId={spotifyUserId} label="Reconectar" onConnected={open} />
        </div>
      )}
    </section>
  );
}

function deriveMode(allowedGenres, bannedGenres) {
  if (allowedGenres.length > 0) return 'allowed';
  if (bannedGenres.length > 0)  return 'banned';
  return 'none';
}

export function AccountConfig() {
  const { spotifyUserId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const accountLabel = location.state?.email || spotifyUserId;
  const { user } = useAuth();
  const { subscription } = useSubscription(user?.uid);

  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [saved, setSaved]             = useState(false);
  const [error, setError]             = useState('');
  const [durationMin, setDurationMin] = useState(0); // 0 = sin límite
  const [genreMode, setGenreMode]     = useState('none');
  const [allowed, setAllowed]         = useState([]);
  const [banned, setBanned]           = useState([]);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [removing, setRemoving]       = useState(false);
  const [removeError, setRemoveError] = useState('');
  const [playlist, setPlaylist]       = useState(null);
  const playlistActive = subscription?.tier === 'vip' && !!playlist; // mismo criterio que RoomMode del back

  useEffect(() => {
    getAccountConfig(spotifyUserId)
      .then((config) => {
        const min = Math.round((config?.maxDurationSeconds ?? 0) / 60);
        setDurationMin(min > 0 && min < 3 ? 3 : min);
        const a = config?.allowedGenres ?? [];
        const b = config?.bannedGenres ?? [];
        setAllowed(a);
        setBanned(b);
        setGenreMode(deriveMode(a, b));
        setPlaylist(config?.playlist ?? null);
      })
      .catch(() => setError('No se pudo cargar la configuración.'))
      .finally(() => setLoading(false));
  }, [spotifyUserId]);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      await updateAccountConfig(spotifyUserId, {
        maxDurationSeconds: durationMin * 60,
        allowedGenres: genreMode === 'allowed' ? allowed : [],
        bannedGenres:  genreMode === 'banned'  ? banned  : [],
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('No se pudo guardar. Intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  useAutosave(save, [durationMin, genreMode, allowed, banned], !loading);

  const handleRemove = async () => {
    setRemoving(true);
    setRemoveError('');
    try {
      await removeSpotifyAccount(spotifyUserId);
      navigate(ROUTES.CONTROL);
    } catch {
      setRemoveError('Error al eliminar la cuenta');
      setRemoving(false);
    }
  };

  const stepBtn = 'flex h-12 w-12 items-center justify-center rounded bg-lounge-raised text-xl text-lounge-stone ' +
    'transition-colors hover:text-lounge-text disabled:cursor-not-allowed disabled:opacity-30';
  const activeMode = GENRE_MODES.find((m) => m.value === genreMode);

  return (
    <div className="relative min-h-dvh overflow-x-hidden bg-lounge-base font-inter text-lounge-text antialiased
                    selection:bg-lounge-brass/30">
      <div aria-hidden className="pointer-events-none fixed inset-0 opacity-[0.035]" style={{ backgroundImage: GRAIN }} />

      {confirmingRemove && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <div role="dialog" aria-modal="true" aria-labelledby="remove-title"
               className="w-full max-w-sm rounded-lg border border-lounge-line bg-lounge-card p-6 shadow-sleeve">
            <h3 id="remove-title" className="font-serif text-2xl font-medium text-lounge-text">¿Eliminar esta cuenta?</h3>
            <p className="mt-3 break-all rounded border border-lounge-line bg-lounge-base px-3 py-2 font-mono text-xs
                          tracking-wider text-lounge-text">
              {accountLabel}
            </p>
            <p className="mt-3 text-sm text-lounge-stone">Esta acción no se puede deshacer.</p>
            {removeError && (
              <p role="alert" className="mt-3 rounded border border-stop/30 bg-stop/10 px-4 py-2.5 text-sm text-stop-soft">
                {removeError}
              </p>
            )}
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setConfirmingRemove(false)}
                className="rounded border border-lounge-line bg-lounge-raised px-4 py-2.5 font-mono text-[11px] uppercase
                           tracking-[0.15em] text-lounge-text transition-colors hover:border-lounge-stone/40"
              >
                Cancelar
              </button>
              <button
                onClick={handleRemove}
                disabled={removing}
                className="rounded bg-stop px-4 py-2.5 text-sm font-semibold text-lounge-base transition-colors
                           hover:bg-stop/90 active:scale-[0.98] disabled:opacity-50"
              >
                {removing ? 'Eliminando…' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <header className="sticky top-0 z-40 border-b border-lounge-line bg-lounge-base/75
                         pt-[env(safe-area-inset-top)] backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between gap-4 px-5">
          <button
            onClick={() => navigate(ROUTES.CONTROL)}
            className="-ml-2 flex items-center gap-2 px-2 py-2 font-mono text-[11px] uppercase tracking-[0.15em]
                       text-lounge-stone transition-colors hover:text-lounge-text"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Volver al panel
          </button>
          <Wordmark className="text-[13px]" />
        </div>
      </header>

      <main className="relative mx-auto flex max-w-3xl flex-col gap-6 px-5 pb-16 pt-8 lg:pt-12">
        <div className="mb-2">
          <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-lounge-brass">
            Configuración de cuenta
          </span>
          <h1 className="break-all font-serif text-[28px] font-semibold leading-tight tracking-tight sm:text-4xl">
            {accountLabel}
          </h1>
          <p className="mt-2 h-4 font-mono text-[11px] tracking-wider">
            {saving && <span role="status" className="text-lounge-stone">Guardando…</span>}
            {!saving && saved && <span role="status" className="text-felt">Guardado</span>}
            {!saving && error && <span role="alert" className="text-stop-soft">{error}</span>}
          </p>
        </div>

        {loading ? (
          <div className="flex animate-pulse flex-col gap-6 motion-reduce:animate-none" aria-busy="true">
            <div className="h-44 rounded-lg bg-lounge-card" />
            <div className="h-72 rounded-lg bg-lounge-card" />
          </div>
        ) : (
          <>
            {(subscription?.tier === 'vip' || playlist) && (
              <PlaylistSection spotifyUserId={spotifyUserId} playlist={playlist} onChange={setPlaylist} />
            )}

            {/* Con playlist activa (y plan VIP) la sala ofrece toda la playlist: el back no aplica filtros.
                fieldset disabled bloquea todos los controles de adentro. */}
            <fieldset disabled={playlistActive} aria-describedby={playlistActive ? 'filters-off' : undefined}
                      className={playlistActive ? 'flex min-w-0 flex-col gap-6' : 'contents'}>
            {playlistActive && (
              <p id="filters-off" className="rounded-lg border border-lounge-brass/30 bg-lounge-brass/10 px-4 py-3
                                             text-sm text-lounge-text">
                Con la playlist activa no se aplican los filtros de géneros ni de duración
              </p>
            )}
            <div className={playlistActive ? 'flex flex-col gap-6 opacity-50' : 'contents'}>
            <section aria-labelledby="genres-title" className={CARD}>
              <h2 id="genres-title" className="font-serif text-[22px] font-medium">Filtro de géneros</h2>

              <div role="radiogroup" aria-label="Modo de filtrado"
                   className="mt-5 grid grid-cols-3 gap-1 rounded-md border border-lounge-line bg-lounge-base p-1">
                {GENRE_MODES.map((mode) => {
                  const active = genreMode === mode.value;
                  return (
                    <button
                      key={mode.value}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setGenreMode(mode.value)}
                      className={
                        'min-h-[44px] rounded px-2 py-2 font-mono text-[11px] uppercase leading-tight tracking-wider transition-colors ' +
                        (active ? 'bg-lounge-brass font-semibold text-lounge-base' : 'text-lounge-stone hover:text-lounge-text')
                      }
                    >
                      {mode.label}
                    </button>
                  );
                })}
              </div>
              <p className="mt-3 text-sm text-lounge-stone">{activeMode?.desc}</p>

              {genreMode !== 'none' && (
                <div className="mt-6">
                  <GenreGrid
                    key={genreMode}
                    selected={genreMode === 'allowed' ? allowed : banned}
                    onChange={genreMode === 'allowed' ? setAllowed : setBanned}
                    mode={genreMode}
                  />
                </div>
              )}
            </section>

            <section aria-labelledby="duration-title" className={CARD}>
              <h2 id="duration-title" className="font-serif text-[22px] font-medium">Duración máxima</h2>
              <p className="mt-2 text-sm leading-relaxed text-lounge-stone">
                Las canciones más largas no aparecerán en los resultados de búsqueda.
              </p>
              <div className="mt-5 flex items-center justify-between gap-2 rounded-md border border-lounge-line bg-lounge-base p-1.5">
                <button type="button" aria-label="Reducir duración" onClick={() => setDurationMin((v) => (v <= 3 ? 0 : v - 1))}
                        disabled={durationMin <= 0} className={stepBtn}>−</button>
                <div className="text-center" aria-live="polite">
                  <p className="font-mono text-xl tabular-nums text-lounge-brass">
                    {durationMin === 0 ? 'Sin límite' : `${durationMin} min`}
                  </p>
                  <p className="font-mono text-[11px] tracking-wider text-lounge-stone">Mínimo 3 min</p>
                </div>
                <button type="button" aria-label="Aumentar duración" onClick={() => setDurationMin((v) => (v === 0 ? 3 : Math.min(30, v + 1)))}
                        disabled={durationMin >= 30} className={stepBtn}>+</button>
              </div>
            </section>
            </div>
            </fieldset>

            <section aria-labelledby="danger-title" className="mt-6 border-t border-lounge-line pt-8">
              <span id="danger-title" className="mb-3 block font-mono text-[11px] uppercase tracking-[0.2em] text-stop-soft">
                Zona de peligro
              </span>
              <div className="flex flex-col gap-4 rounded-lg border border-stop/30 bg-stop/10 p-5
                              sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="flex items-center gap-2 font-medium text-lounge-text">
                    <TrashIcon className="h-[18px] w-[18px] text-stop-soft" />
                    Eliminar cuenta
                  </p>
                  <p className="mt-1 text-sm text-lounge-stone">
                    Se desconecta esta cuenta de Spotify y se elimina su QR principal. No se puede deshacer.
                  </p>
                </div>
                <button
                  onClick={() => setConfirmingRemove(true)}
                  className="shrink-0 rounded border border-stop/50 px-4 py-2.5 font-mono text-[11px] uppercase
                             tracking-[0.15em] text-stop-soft transition-colors hover:bg-stop/15"
                >
                  Eliminar cuenta
                </button>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
